import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogOut, FiUser, FiUserPlus, FiUsers, FiMenu, FiX, FiSettings } = FiIcons;

const Header = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isPeter = user?.system_role === 'superadmin';
  const canAllocate = isPeter || user?.system_role === 'admin';

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 md:px-6 md:py-4">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/" className="text-decoration-none">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">BPA Success Center</h1>
            <p className="text-xs md:text-sm text-gray-600">Digital Marketing Coach Platform</p>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          {/* Admin links */}
          <div className="flex items-center space-x-4">
            <Link
              to="/members"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
            >
              <SafeIcon icon={FiUsers} className="w-5 h-5" />
              <span className="hidden md:inline">Members</span>
            </Link>
            <Link
              to="/add-member"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
            >
              <SafeIcon icon={FiUserPlus} className="w-5 h-5" />
              <span className="hidden md:inline">Add Member</span>
            </Link>
            {canAllocate && <Link
              to="/manage-coaches"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
            >
              <SafeIcon icon={FiUser} className="w-5 h-5" />
              <span className="hidden md:inline">{isPeter ? 'Admins' : 'Coaches'}</span>
            </Link>}

            {/* Only show App Settings for Peter */}
            {isPeter && (
              <Link
                to="/settings"
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <SafeIcon icon={FiSettings} className="w-5 h-5" />
                <span className="hidden md:inline">App Settings</span>
              </Link>
            )}
          </div>

          <div className="border-l border-gray-300 h-8"></div>

          {/* User profile */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiUser} className="text-primary-600" />
              </div>
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center">
          <div className="flex items-center space-x-2 mr-2">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiUser} className="text-primary-600" />
            </div>
          </div>
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100"
          >
            <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-3 pt-3 border-t border-gray-200">
          <nav className="grid grid-cols-2 gap-2">
            <Link
              to="/members"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center space-x-2 p-3 rounded-md hover:bg-gray-100"
            >
              <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary-600" />
              <span>Members</span>
            </Link>
            <Link
              to="/add-member"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center space-x-2 p-3 rounded-md hover:bg-gray-100"
            >
              <SafeIcon icon={FiUserPlus} className="w-5 h-5 text-primary-600" />
              <span>Add Member</span>
            </Link>
            {canAllocate && <Link
              to="/manage-coaches"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center space-x-2 p-3 rounded-md hover:bg-gray-100"
            >
              <SafeIcon icon={FiUser} className="w-5 h-5 text-primary-600" />
              <span>{isPeter ? 'Admins' : 'Coaches'}</span>
            </Link>}

            {/* Only show App Settings for Peter */}
            {isPeter && (
              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-2 p-3 rounded-md hover:bg-gray-100"
              >
                <SafeIcon icon={FiSettings} className="w-5 h-5 text-primary-600" />
                <span>App Settings</span>
              </Link>
            )}

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                logout();
              }}
              className="flex items-center space-x-2 p-3 text-left rounded-md hover:bg-gray-100 text-red-600"
            >
              <SafeIcon icon={FiLogOut} className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </nav>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.role}</p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
